import { useState, useEffect } from "react"

import classnames from "classnames"
import styles from "./index.module.scss"
import CancelConfirmation from "./cancel_confirmation.tsx"
import CustomerSupport from "./customer_support.tsx"
import ExclusiveOffer from "./exclusive_offer.tsx"
import Header from "./header.tsx"
import ProFeatureCheck from "./pro_feature_check.tsx"
import Survey from "./survey.tsx"
import SwitchYourPlan from "./switch_your_plan.tsx"

import { post } from "shared/lib/api"

import { captureMessage } from "@sentry/react"

interface Assets {
  iftttSvgUrl: string
}

interface UserSubscription {
  hasDiscount: boolean
  paymentInterval: string
  tier: string
}

interface Props {
  assets: Assets
  userSubscription: UserSubscription
  expirationDate: string
  maxFreeApplets: number
  proPlusProductId: string
  proProductId: string
  urls: {
    billing: string
    claimOffer: string
    explore: string
    processProCancellation: string
  }
}

const BillingCancelContainer = (props: Props) => {
  const [surveyOption, selectSurveryOption] = useState("")
  const [otherText, setOtherText] = useState("")
  const [view, updateView] = useState("initial")
  const [previousView, setPreviousView] = useState("")

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [view])

  const tierName = props.userSubscription.tier === "pro" ? "Pro+" : "Pro"

  const goToView = (next: string) => {
    setPreviousView(view)
    updateView(next)
  }

  // The datalake only records users who complete cancellation, so the save outcomes
  // (downgrade, discount) are invisible server-side and have to be counted here.
  const trackCancelAction = (action: string, extraTags = {}) => {
    window.App.Utils?.logCustomDatadogAction?.(action, {
      survey_option: surveyOption || "none",
      tier: props.userSubscription.tier,
      view,
      previous_view: previousView || "none",
      ...extraTags,
    })
  }

  const trackMutationError = (mutationName, error, userFeedback, updateLoadingState) => {
    updateLoadingState?.(false)
    if (window.App.allowDebugErrorMessages) console.warn(error)
    captureMessage(`${mutationName} failed`, { extra: error })
    window.appendFlash(userFeedback, "danger")
  }

  const handleCancellation = updateLoading => {
    trackCancelAction("click_cancel_subscription")
    updateLoading?.(true)
    post(props.urls.processProCancellation, { survey_option: surveyOption, other_reason: otherText || undefined }).then(data => {
      if (data.ok) {
        goToView("plan_cancelled")
      } else {
        trackMutationError(
          "userSubscriptionCancellation",
          data.error,
          "There was a problem cancelling your subscription. Please contact support for more information.",
          updateLoading
        )
      }
    })
  }

  const onBackClick = () => {
    switch (view) {
      case "initial":
        window.App.Scripts.BackButton(props.urls.billing)
        break
      case "plan_cancelled":
        window.App.Scripts.BackButton(props.urls.billing, { reload: true })
        break
      case "survey":
        selectSurveryOption("")
        goToView("initial")
        break
      case "exclusive_offer":
        // Price objectors reach the offer via the plan-switch screen, so back has to
        // return them to it rather than skipping a rung of the ladder.
        goToView(previousView === "switch_your_plan" ? "switch_your_plan" : "survey")
        break
      case "switch_your_plan":
      case "customer_support":
        goToView("survey")
        break
    }
  }

  const choseView = () => {
    switch (view) {
      case "initial":
        return (
          <ProFeatureCheck
            billingUrl={props.urls.billing}
            expirationDate={props.expirationDate}
            maxFreeApplets={props.maxFreeApplets}
            moveNextView={() => goToView("survey")}
            tierName={tierName}
          />
        )
      case "survey":
        return (
          <Survey
            selectSurveryOption={selectSurveryOption}
            moveNextView={goToView}
            onOtherTextChange={setOtherText}
            surveyOption={surveyOption}
            tierName={tierName}
            trackCancelAction={trackCancelAction}
          />
        )
      case "customer_support":
        return <CustomerSupport handleCancellation={handleCancellation} />
      case "exclusive_offer":
        return (
          <ExclusiveOffer
            billingUrl={props.urls.billing}
            claimOfferUrl={props.urls.claimOffer}
            exploreUrl={props.urls.explore}
            handleCancellation={handleCancellation}
            tierName={tierName}
            trackCancelAction={trackCancelAction}
            trackMutationError={trackMutationError}
            userSubscription={props.userSubscription}
          />
        )
      case "switch_your_plan": {
        // Price objectors who decline the downgrade fall through to the discount instead
        // of cancelling outright, so they see both offers. Every other reason that reaches
        // this screen (wrong_plan) keeps the original decline-is-cancel behavior.
        const declinesToOffer = surveyOption === "plans_too_pricy"
        return (
          <SwitchYourPlan
            declineLabel={declinesToOffer ? "No thanks" : "No thanks, just cancel"}
            exploreUrl={props.urls.explore}
            onDecline={declinesToOffer ? () => goToView("exclusive_offer") : handleCancellation}
            productId={tierName === "Pro+" ? props.proProductId : props.proPlusProductId}
            tierName={tierName}
            trackCancelAction={trackCancelAction}
            trackMutationError={trackMutationError}
          />
        )
      }
      case "plan_cancelled":
        return (
          <CancelConfirmation
            expirationDate={props.expirationDate}
            exploreUrl={props.urls.explore}
            tierName={tierName}
          />
        )
    }
  }

  return (
    <section className={classnames(styles.cancel_plan_container, { [styles.plan_cancelled]: view === "plan_cancelled" })}>
      <Header onBackClick={onBackClick} />
      {choseView()}
    </section>
  )
}

export default BillingCancelContainer
